import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  Box,
  Typography,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Alert,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  TableSortLabel,
  MenuItem,
  Menu,
  Checkbox,
  Toolbar,
  alpha
} from '@mui/material';
import {
  Business,
  CheckCircle,
  Settings as SettingsIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  TrendingUp as TrendingUpIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ModernCard from '../components/Common/ModernCard';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import ModernLoader from '../components/Common/ModernLoader';
import { logger } from '../utils/logger';

interface CompanyFeatures {
  membership_enabled: boolean;
  events_enabled: boolean;
  coupons_enabled: boolean;
}

interface Company {
  company_id: string;
  company_name: string;
  display_name?: string;
  industry?: string;
  company_size?: string;
  primary_email: string;
  phone?: string;
  website?: string;
  tier: string;
  status: string;
  monthly_pass_limit: string;
  monthly_passes_used: string;
  template_limit: string;
  user_limit: string;
  user_count: number;
  location_limit: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  settings?: {
    features?: CompanyFeatures;
  };
}

const CompanyManagement: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [featuresDialogOpen, setFeaturesDialogOpen] = useState(false);
  const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [tierChangeDialogOpen, setTierChangeDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [tierMenuAnchor, setTierMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Company;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [companyFeatures, setCompanyFeatures] = useState<CompanyFeatures>({
    membership_enabled: true,
    events_enabled: true,
    coupons_enabled: true
  });

  // Bulk operations state
  const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Add Company Form State
  const [newCompanyData, setNewCompanyData] = useState({
    company_name: '',
    display_name: '',
    primary_email: '',
    industry: '',
    company_size: '',
    phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'RS',
    tier: 'free',
    settings: {
      features: {
        membership_enabled: true,
        events_enabled: false,
        coupons_enabled: false
      }
    }
  });
  const [createCompanyLoading, setCreateCompanyLoading] = useState(false);
  const [createCompanyError, setCreateCompanyError] = useState<string | null>(null);

  // Tier options for company upgrades/downgrades
  const tierOptions = [
    { value: 'free', label: 'Free - €0/mesec', description: '10 pass-ova (hard limit)', color: 'default' },
    { value: 'basic', label: 'Basic - €25/mesec', description: '100 pass-ova uključeno', color: 'primary' },
    { value: 'premium', label: 'Premium - €100/mesec', description: '500 pass-ova uključeno', color: 'secondary' },
    { value: 'enterprise', label: 'Enterprise - €1,000/mesec', description: '5,000 pass-ova uključeno', color: 'success' }
  ];

  useEffect(() => {
    if (user?.role !== 'admin') {
      setError('Access denied. Admin role required.');
      setLoading(false);
      return;
    }
    fetchCompanies();
  }, [user]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies/');
      setCompanies(response.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load companies';
      setError(errorMessage);
      logger.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTierChange = (company: Company, event: React.MouseEvent<HTMLElement>) => {
    setSelectedCompany(company);
    setSelectedTier(company.tier);
    setTierMenuAnchor(event.currentTarget);
  };

  const handleTierMenuClose = () => {
    setTierMenuAnchor(null);
    setSelectedCompany(null);
  };

  const handleTierSelect = (newTier: string) => {
    setSelectedTier(newTier);
    setTierMenuAnchor(null);
    setTierChangeDialogOpen(true);
  };

  const handleConfirmTierChange = async () => {
    if (!selectedCompany || !selectedTier) return;

    try {
      await api.patch(`/companies/${selectedCompany.company_id}/tier`, { tier: selectedTier });

      // Update local state
      setCompanies(prev =>
        prev.map(company =>
          company.company_id === selectedCompany.company_id
            ? { ...company, tier: selectedTier }
            : company
        )
      );

      logger.log(`✅ Successfully updated ${selectedCompany.company_name} tier to ${selectedTier.toUpperCase()}`);

      setTierChangeDialogOpen(false);
      setSelectedCompany(null);
      setSelectedTier('');

      // Clear any previous errors
      setError(null);

    } catch (err) {
      logger.error('❌ Error updating tier:', err);
      setError(err instanceof Error ? err.message : 'Failed to update tier');
    }
  };

  const handleManageFeatures = (company: Company) => {
    setSelectedCompany(company);
    setCompanyFeatures(company.settings?.features || {
      membership_enabled: true,
      events_enabled: true,
      coupons_enabled: true
    });
    setFeaturesDialogOpen(true);
  };

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!companyToDelete) return;

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await api.delete(`/companies/${companyToDelete.company_id}`);

      // Success - refresh companies list
      await fetchCompanies();
      setDeleteDialogOpen(false);
      setCompanyToDelete(null);
    } catch (err: any) {
      logger.error('Delete company error:', err);
      setDeleteError(err.message || 'Failed to delete company');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
    setDeleteError(null);
  };

  const handleSaveFeatures = async () => {
    if (!selectedCompany) return;

    try {
      await api.patch(`/companies/${selectedCompany.company_id}/features`, { features: companyFeatures });

      // Update local state
      setCompanies(prev => prev.map(company =>
        company.company_id === selectedCompany.company_id
          ? {
              ...company,
              settings: {
                ...company.settings,
                features: companyFeatures
              }
            }
          : company
      ));

      setFeaturesDialogOpen(false);
      setSelectedCompany(null);
    } catch (err) {
      setError('Failed to update company features');
      logger.error('Error updating features:', err);
    }
  };

  // Search and sorting functions
  const handleSort = (key: keyof Company) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedCompanies = React.useMemo(() => {
    let filtered = companies.filter(company =>
      company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.primary_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [companies, searchTerm, sortConfig]);

  // Bulk operations functions
  const handleSelectAll = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedCompanies(new Set(filteredAndSortedCompanies.map(company => company.company_id)));
    } else {
      setSelectedCompanies(new Set());
    }
  }, [filteredAndSortedCompanies]);

  const handleSelectCompany = useCallback((companyId: string) => {
    setSelectedCompanies(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(companyId)) {
        newSelected.delete(companyId);
      } else {
        newSelected.add(companyId);
      }
      return newSelected;
    });
  }, []);

  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedCompanies.size === 0) return;

    setBulkLoading(true);
    const companyIds = Array.from(selectedCompanies);

    // Optimistic update
    setCompanies(prev => prev.map(company =>
      companyIds.includes(company.company_id)
        ? { ...company, status: newStatus, is_active: newStatus === 'active' }
        : company
    ));

    try {
      await Promise.all(
        companyIds.map(id =>
          api.patch(`/companies/${id}/status`, { status: newStatus })
        )
      );

      setSelectedCompanies(new Set());
    } catch (error) {
      logger.error('Bulk status update failed:', error);
      fetchCompanies();
    } finally {
      setBulkLoading(false);
    }
  };

  // Bulk tier update - currently unused (removed from UI)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBulkTierUpdate = async (newTier: string) => {
    if (selectedCompanies.size === 0) return;

    setBulkLoading(true);
    const companyIds = Array.from(selectedCompanies);

    // Optimistic update
    setCompanies(prev => prev.map(company =>
      companyIds.includes(company.company_id)
        ? { ...company, tier: newTier }
        : company
    ));

    try {
      await Promise.all(
        companyIds.map(id =>
          api.patch(`/companies/${id}/tier`, { tier: newTier })
        )
      );

      setSelectedCompanies(new Set());
    } catch (error) {
      logger.error('Bulk tier update failed:', error);
      fetchCompanies();
    } finally {
      setBulkLoading(false);
    }
  };

  const isSelected = (companyId: string) => selectedCompanies.has(companyId);
  const isIndeterminate = selectedCompanies.size > 0 && selectedCompanies.size < filteredAndSortedCompanies.length;
  const isAllSelected = filteredAndSortedCompanies.length > 0 && selectedCompanies.size === filteredAndSortedCompanies.length;

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCreateCompany = async () => {
    // Validation
    if (!newCompanyData.company_name.trim()) {
      setCreateCompanyError('Naziv kompanije je obavezan');
      return;
    }

    if (!newCompanyData.primary_email.trim()) {
      setCreateCompanyError('Email adresa je obavezna');
      return;
    }

    // Email validation - Simple pattern to avoid ReDoS vulnerability
    // Checks: non-empty local part @ non-empty domain . non-empty TLD
    const emailParts = newCompanyData.primary_email.split('@');
    const isValidEmail =
      emailParts.length === 2 &&
      emailParts[0].length > 0 &&
      emailParts[1].includes('.') &&
      emailParts[1].split('.').every(part => part.length > 0);

    if (!isValidEmail) {
      setCreateCompanyError('Unesite validnu email adresu');
      return;
    }

    setCreateCompanyLoading(true);
    setCreateCompanyError(null);

    try {
      const response = await api.post('/companies/', newCompanyData);
      const newCompany = response.data;

      // Add to companies list
      setCompanies(prev => [newCompany, ...prev]);

      // Reset form
      setNewCompanyData({
        company_name: '',
        display_name: '',
        primary_email: '',
        industry: '',
        company_size: '',
        phone: '',
        website: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'RS',
        tier: 'free',
        settings: {
          features: {
            membership_enabled: true,
            events_enabled: false,
            coupons_enabled: false
          }
        }
      });

      // Close dialog
      setAddCompanyDialogOpen(false);

      logger.log('✅ Successfully created company:', newCompany.company_name);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create company';
      setCreateCompanyError(errorMessage);
      logger.error('❌ Error creating company:', err);
    } finally {
      setCreateCompanyLoading(false);
    }
  };

  const handleCloseAddCompanyDialog = () => {
    setAddCompanyDialogOpen(false);
    setCreateCompanyError(null);
    setNewCompanyData({
      company_name: '',
      display_name: '',
      primary_email: '',
      industry: '',
      company_size: '',
      phone: '',
      website: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'RS',
      tier: 'free',
      settings: {
        features: {
          membership_enabled: true,
          events_enabled: false,
          coupons_enabled: false
        }
      }
    });
  };

  const getTierChipColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'enterprise': return 'error';
      case 'premium': return 'warning';
      case 'basic': return 'info';
      default: return 'default';
    }
  };

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'trial': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ModernLoader size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      m: 0,
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #f7faff 0%, #dfeafc 50%, #abc3ea 100%)',
      position: 'relative',
    }}>
      <ResponsivePageHeader
        title="Upravljanje Kompanijama"
        subtitle="Konfiguriši funkcionalnosti za svaku kompaniju pojedinačno"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddCompanyDialogOpen(true)}
          >
            Dodaj kompaniju
          </Button>
        }
      />

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Konfiguriši funkcionalnosti za svaku kompaniju pojedinačno
      </Typography>

      <ModernCard>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Lista kompanija ({filteredAndSortedCompanies.length} od {companies.length})
            </Typography>
          </Box>

          {/* Search Bar */}
          <Box mb={3}>
            <TextField
              fullWidth
              placeholder="Pretraži kompanije po imenu, email-u, ili industriji..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={clearSearch}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                }
              }}
            />
          </Box>

          {/* Bulk Operations Toolbar */}
          {selectedCompanies.size > 0 && (
            <Toolbar
              sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                borderRadius: 1,
                mb: 2,
                minHeight: { xs: 48, sm: 64 }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '1 1 100%',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: 1
              }}>
                <Typography
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 auto' },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    mb: { xs: 1, sm: 0 }
                  }}
                  color="inherit"
                  variant="subtitle1"
                  component="div"
                >
                  {selectedCompanies.size} odabrano
                </Typography>

                <Box sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  width: { xs: '100%', sm: 'auto' }
                }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleBulkStatusUpdate('active')}
                    disabled={bulkLoading}
                    sx={{
                      minWidth: { xs: 'auto', sm: 100 },
                      color: 'white !important'
                    }}
                  >
                    Aktiviraj
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={<PauseIcon />}
                    onClick={() => handleBulkStatusUpdate('suspended')}
                    disabled={bulkLoading}
                    sx={{
                      minWidth: { xs: 'auto', sm: 100 },
                      color: 'white !important'
                    }}
                  >
                    Suspenduj
                  </Button>

                  <IconButton
                    size="small"
                    onClick={() => setSelectedCompanies(new Set())}
                    sx={{ ml: { xs: 0, sm: 1 } }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </Toolbar>
          )}

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '12px',
              overflow: 'hidden',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                '0%': {
                  opacity: 0,
                  transform: 'translateY(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell padding="checkbox" sx={{ fontWeight: 600 }}>
                    <Checkbox
                      color="primary"
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      aria-label="select all companies"
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortConfig?.key === 'company_name'}
                      direction={sortConfig?.key === 'company_name' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('company_name')}
                    >
                      Kompanija
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortConfig?.key === 'company_id'}
                      direction={sortConfig?.key === 'company_id' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('company_id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortConfig?.key === 'tier'}
                      direction={sortConfig?.key === 'tier' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('tier')}
                    >
                      Tier
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <TableSortLabel
                      active={sortConfig?.key === 'status'}
                      direction={sortConfig?.key === 'status' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Upotreba</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Funkcionalnosti</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Akcije</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedCompanies.map((company) => {
                  const features = company.settings?.features;
                  const activeFeatures = [
                    features?.membership_enabled && 'Membership',
                    features?.events_enabled && 'Events',
                    features?.coupons_enabled && 'Coupons'
                  ].filter(Boolean);

                  return (
                    <TableRow
                      key={company.company_id}
                      hover
                      selected={isSelected(company.company_id)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(20, 38, 74, 0.04)',
                          transform: 'scale(1.005)',
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected(company.company_id)}
                          onChange={() => handleSelectCompany(company.company_id)}
                          aria-labelledby={`company-${company.company_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Avatar
                            sx={{
                              background: 'linear-gradient(135deg, #0a1525 0%, #14264a 100%)',
                              width: 40,
                              height: 40,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: '0 4px 20px rgba(20, 38, 74, 0.3)',
                              }
                            }}
                          >
                            <Business />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {company.display_name || company.company_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {company.primary_email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            padding: '4px 8px',
                            backgroundColor: 'grey.50',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: 'grey.200',
                            cursor: 'pointer',
                            maxWidth: '140px',
                            '&:hover': {
                              backgroundColor: 'grey.100',
                              borderColor: 'primary.main',
                            }
                          }}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(company.company_id);
                              // You could add a toast notification here
                              logger.log('Company ID copied:', company.company_id);
                            } catch (err) {
                              logger.error('Failed to copy:', err);
                            }
                          }}
                          title="Click to copy ID"
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1
                            }}
                          >
                            {company.company_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={company.tier.toUpperCase()}
                            color={getTierChipColor(company.tier)}
                            size="small"
                            clickable
                            onClick={(e) => handleTierChange(company, e)}
                            icon={<TrendingUpIcon />}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={company.status.toUpperCase()}
                          color={getStatusChipColor(company.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5} sx={{ minWidth: 140 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Users:</Typography>
                            <Chip label={`${company.user_count} / ${company.user_limit}`} size="small" variant="outlined" />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Passes:</Typography>
                            <Chip label={`${company.monthly_passes_used} / ${company.monthly_pass_limit}`} size="small" variant="outlined" />
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>Locations:</Typography>
                            <Chip label={`0 / ${company.location_limit}`} size="small" variant="outlined" />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {activeFeatures.length > 0 ? (
                            activeFeatures.map((feature, idx) => (
                              <Chip
                                key={`${company.company_id}-${feature}-${idx}`}
                                label={feature}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Nema aktivnih
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Upravljaj funkcionalnostima">
                            <IconButton
                              onClick={() => handleManageFeatures(company)}
                              color="primary"
                              size="small"
                              sx={{
                                transition: 'all 0.2s ease',
                                borderRadius: '10px',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '& .MuiSvgIcon-root': {
                                    transform: 'rotate(90deg)',
                                  }
                                }
                              }}
                            >
                              <SettingsIcon sx={{ transition: 'transform 0.3s ease' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Promeni tier">
                            <IconButton
                              onClick={(e) => handleTierChange(company, e)}
                              color="secondary"
                              size="small"
                              sx={{
                                transition: 'all 0.2s ease',
                                borderRadius: '10px',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: 'secondary.main',
                                  color: 'white',
                                  '& .MuiSvgIcon-root': {
                                    transform: 'rotate(15deg)',
                                  }
                                }
                              }}
                            >
                              <TrendingUpIcon sx={{ transition: 'transform 0.3s ease' }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Obriši kompaniju">
                            <IconButton
                              onClick={() => handleDeleteClick(company)}
                              color="error"
                              size="small"
                              sx={{
                                transition: 'all 0.2s ease',
                                borderRadius: '10px',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                  backgroundColor: 'error.main',
                                  color: 'white',
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </ModernCard>

      {/* Features Management Dialog */}
      <Dialog
        open={featuresDialogOpen}
        onClose={() => setFeaturesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Business color="primary" />
            <Box>
              <Typography variant="h6">
                Upravljaj funkcionalnostima
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedCompany?.display_name || selectedCompany?.company_name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box py={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Podesi koje funkcionalnosti će biti dostupne korisniku ove kompanije.
              Isključene funkcionalnosti neće se prikazivati u navigacionom meniju.
            </Typography>

            <Box display="flex" flexDirection="column" gap={3}>
              <Box
                p={2}
                border="1px solid"
                borderColor="divider"
                borderRadius={2}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={companyFeatures.membership_enabled}
                      onChange={(e) => setCompanyFeatures(prev => ({
                        ...prev,
                        membership_enabled: e.target.checked
                      }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Membership Management
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Članstvo, članovi, tier sistemi, bodovi
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box
                p={2}
                border="1px solid"
                borderColor="divider"
                borderRadius={2}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={companyFeatures.events_enabled}
                      onChange={(e) => setCompanyFeatures(prev => ({
                        ...prev,
                        events_enabled: e.target.checked
                      }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Event Management
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Događaji, produkcije, venues, karte
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box
                p={2}
                border="1px solid"
                borderColor="divider"
                borderRadius={2}
                sx={{ backgroundColor: 'grey.50' }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={companyFeatures.coupons_enabled}
                      onChange={(e) => setCompanyFeatures(prev => ({
                        ...prev,
                        coupons_enabled: e.target.checked
                      }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Coupon Management
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Kuponi, kampanje, ponude, redemption
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>

            <Box mt={3} p={2} sx={{ backgroundColor: 'info.light', borderRadius: 1 }}>
              <Typography variant="caption" color="info.main">
                💡 Isključene funkcionalnosti neće biti vidljive u meniju za korisnike ove kompanije
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setFeaturesDialogOpen(false)}
            variant="outlined"
          >
            Otkaži
          </Button>
          <Button
            onClick={handleSaveFeatures}
            variant="contained"
            startIcon={<CheckCircle />}
          >
            Sačuvaj promene
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog
        open={addCompanyDialogOpen}
        onClose={handleCloseAddCompanyDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Business color="primary" />
              <Typography variant="h6">
                Dodaj novu kompaniju
              </Typography>
            </Box>
            <IconButton onClick={handleCloseAddCompanyDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} pt={2}>
            {createCompanyError && (
              <Alert severity="error" onClose={() => setCreateCompanyError(null)}>
                {createCompanyError}
              </Alert>
            )}

            <Alert severity="info">
              <Typography variant="body2">
                Nova kompanija će biti automatski kreirana sa:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
                <li>Odabranim tier planom</li>
                <li>PassKit Membership Program-om</li>
                <li>PassKit Coupon Campaign-om</li>
                <li>Odabranim funkcionalnostima</li>
              </Typography>
            </Alert>

            <TextField
              select
              label="Tier Plan *"
              fullWidth
              value={newCompanyData.tier}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, tier: e.target.value }))}
              disabled={createCompanyLoading}
              helperText="Izaberite tier plan za kompaniju"
            >
              <MenuItem value="free">
                <Box>
                  <Typography variant="body1" fontWeight="600">Free - €0/mesec</Typography>
                  <Typography variant="caption" color="text.secondary">10 pass-ova (hard limit)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="basic">
                <Box>
                  <Typography variant="body1" fontWeight="600">Basic - €25/mesec</Typography>
                  <Typography variant="caption" color="text.secondary">100 pass-ova uključeno (auto-upgrade na 500)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="premium">
                <Box>
                  <Typography variant="body1" fontWeight="600">Premium - €100/mesec</Typography>
                  <Typography variant="caption" color="text.secondary">500 pass-ova uključeno (auto-upgrade na 5,000)</Typography>
                </Box>
              </MenuItem>
              <MenuItem value="enterprise">
                <Box>
                  <Typography variant="body1" fontWeight="600">Enterprise - €1,000/mesec</Typography>
                  <Typography variant="caption" color="text.secondary">5,000 pass-ova uključeno (unlimited)</Typography>
                </Box>
              </MenuItem>
            </TextField>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Funkcionalnosti
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCompanyData.settings.features.membership_enabled}
                      onChange={(e) => setNewCompanyData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          features: {
                            ...prev.settings.features,
                            membership_enabled: e.target.checked
                          }
                        }
                      }))}
                      disabled={createCompanyLoading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Membership Cards</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Loyalty kartic, članarine, VIP programi
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCompanyData.settings.features.events_enabled}
                      onChange={(e) => setNewCompanyData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          features: {
                            ...prev.settings.features,
                            events_enabled: e.target.checked
                          }
                        }
                      }))}
                      disabled={createCompanyLoading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Event Tickets</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ulaznice za događaje, konferencije, koncerte
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCompanyData.settings.features.coupons_enabled}
                      onChange={(e) => setNewCompanyData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          features: {
                            ...prev.settings.features,
                            coupons_enabled: e.target.checked
                          }
                        }
                      }))}
                      disabled={createCompanyLoading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">Coupons & Offers</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Kuponi, popusti, promocije
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>

            <TextField
              label="Naziv kompanije *"
              fullWidth
              placeholder="Unesite naziv kompanije"
              variant="outlined"
              value={newCompanyData.company_name}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, company_name: e.target.value }))}
              required
              disabled={createCompanyLoading}
            />

            <TextField
              label="Display name"
              fullWidth
              placeholder="Prikazni naziv kompanije (opciono)"
              variant="outlined"
              value={newCompanyData.display_name}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, display_name: e.target.value }))}
              disabled={createCompanyLoading}
              helperText="Ako nije uneto, koristiće se naziv kompanije"
            />

            <TextField
              label="Email adresa *"
              type="email"
              fullWidth
              placeholder="admin@kompanija.com"
              variant="outlined"
              value={newCompanyData.primary_email}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, primary_email: e.target.value }))}
              required
              disabled={createCompanyLoading}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Industrija"
                fullWidth
                placeholder="IT, Healthcare, Finance..."
                variant="outlined"
                value={newCompanyData.industry}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, industry: e.target.value }))}
                disabled={createCompanyLoading}
              />

              <TextField
                label="Veličina kompanije"
                fullWidth
                placeholder="1-10, 11-50, 51-200..."
                variant="outlined"
                value={newCompanyData.company_size}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, company_size: e.target.value }))}
                disabled={createCompanyLoading}
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Telefon"
                fullWidth
                placeholder="+381 11 123 4567"
                variant="outlined"
                value={newCompanyData.phone}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, phone: e.target.value }))}
                disabled={createCompanyLoading}
              />
            </Box>

            <TextField
              label="Website"
              fullWidth
              placeholder="https://kompanija.com"
              variant="outlined"
              value={newCompanyData.website}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, website: e.target.value }))}
              disabled={createCompanyLoading}
            />

            <TextField
              label="Adresa (Linija 1)"
              fullWidth
              placeholder="Ulica i broj"
              variant="outlined"
              value={newCompanyData.address_line1}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, address_line1: e.target.value }))}
              disabled={createCompanyLoading}
            />

            <TextField
              label="Adresa (Linija 2)"
              fullWidth
              placeholder="Stan, suite, itd. (opciono)"
              variant="outlined"
              value={newCompanyData.address_line2}
              onChange={(e) => setNewCompanyData(prev => ({ ...prev, address_line2: e.target.value }))}
              disabled={createCompanyLoading}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Grad"
                fullWidth
                placeholder="Beograd"
                variant="outlined"
                value={newCompanyData.city}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, city: e.target.value }))}
                disabled={createCompanyLoading}
              />

              <TextField
                label="Pokrajina/Регион"
                fullWidth
                placeholder="Srbija, Vojvodina..."
                variant="outlined"
                value={newCompanyData.state}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, state: e.target.value }))}
                disabled={createCompanyLoading}
              />
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="Poštanski broj"
                fullWidth
                placeholder="11000"
                variant="outlined"
                value={newCompanyData.postal_code}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, postal_code: e.target.value }))}
                disabled={createCompanyLoading}
              />

              <TextField
                label="Država"
                fullWidth
                placeholder="RS"
                variant="outlined"
                value={newCompanyData.country}
                onChange={(e) => setNewCompanyData(prev => ({ ...prev, country: e.target.value }))}
                disabled={createCompanyLoading}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseAddCompanyDialog}
            variant="outlined"
            disabled={createCompanyLoading}
          >
            Otkaži
          </Button>
          <Button
            onClick={handleCreateCompany}
            variant="contained"
            startIcon={createCompanyLoading ? <ModernLoader size={20} /> : <AddIcon />}
            disabled={createCompanyLoading}
          >
            {createCompanyLoading ? 'Kreiranje...' : 'Kreiraj kompaniju'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tier Change Menu */}
      <Menu
        anchorEl={tierMenuAnchor}
        open={Boolean(tierMenuAnchor)}
        onClose={handleTierMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {tierOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleTierSelect(option.value)}
            disabled={option.value === selectedCompany?.tier}
            sx={{
              minWidth: 200,
              flexDirection: 'column',
              alignItems: 'flex-start',
              py: 1.5
            }}
          >
            <Box display="flex" alignItems="center" gap={1} width="100%">
              <Chip
                label={option.label}
                color={option.color as any}
                size="small"
              />
              {option.value === selectedCompany?.tier && (
                <Typography variant="caption" color="text.secondary">
                  (trenutni)
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {option.description}
            </Typography>
          </MenuItem>
        ))}
      </Menu>

      {/* Delete Company Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon color="error" />
            <Typography variant="h6">
              Obriši kompaniju
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ova akcija je nepovratna i obrisaće sve podatke kompanije.
          </Alert>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          <Typography variant="body1" gutterBottom>
            Da li ste sigurni da želite da obrišete kompaniju:
          </Typography>

          <Box
            p={2}
            my={2}
            bgcolor="grey.100"
            borderRadius={2}
            border="1px solid"
            borderColor="error.main"
          >
            <Typography variant="h6" color="error">
              {companyToDelete?.display_name || companyToDelete?.company_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {companyToDelete?.company_id}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pre brisanja kompanije, neophodno je:
          </Typography>

          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Obrisati sve korisnike kompanije
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Odlinkovati sve programe (membership)
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Odlinkovati sve kampanje (coupons)
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            disabled={deleteLoading}
          >
            Otkaži
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={deleteLoading ? <ModernLoader size={20} /> : <DeleteIcon />}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Brisanje...' : 'Obriši kompaniju'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tier Change Confirmation Dialog */}
      <Dialog
        open={tierChangeDialogOpen}
        onClose={() => setTierChangeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6">
              Promeni tier za kompaniju
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedCompany && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Kompanija:</strong> {selectedCompany.company_name}
              </Typography>

              <Box my={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Trenutni tier:
                </Typography>
                <Chip
                  label={selectedCompany.tier.toUpperCase()}
                  color={getTierChipColor(selectedCompany.tier)}
                  sx={{ mr: 2 }}
                />

                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Novi tier:
                </Typography>
                <Chip
                  label={selectedTier.toUpperCase()}
                  color={getTierChipColor(selectedTier)}
                />
              </Box>

              <Box p={2} sx={{ backgroundColor: 'info.light', borderRadius: 1, opacity: 0.8 }}>
                <Typography variant="body2" color="info.dark">
                  💡 <strong>Napomena:</strong> Promena tier-a će odmah uticati na limite i funkcionalnosti kompanije.
                  Nova ograničenja će biti primenjena od sledećeg meseca.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setTierChangeDialogOpen(false)}
            variant="outlined"
          >
            Otkaži
          </Button>
          <Button
            onClick={handleConfirmTierChange}
            variant="contained"
            startIcon={<TrendingUpIcon />}
            color="primary"
          >
            Potvrdi promenu
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManagement;
